import { Link } from 'react-router-dom';
import { Sparkles, ShieldCheck, Download } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { ThemeToggle } from '../components/ThemeToggle';

export function LandingPage() {
    const { isAuthenticated, isLoading } = useAuth();

    const renderPrimaryCta = () => {
        if (isLoading) {
            return (
                <div className="btn btn-secondary opacity-70 cursor-wait">
                    Checking session...
                </div>
            );
        }
        if (isAuthenticated) {
            return (
                <Link to="/dashboard" className="btn btn-primary">
                    Go to dashboard
                </Link>
            );
        }
        return (
            <Link to="/register" className="btn btn-primary">
                Get started
            </Link>
        );
    };

    const renderSecondaryCta = () => {
        if (isLoading || isAuthenticated) {
            return null;
        }
        return (
            <Link to="/login" className="btn btn-secondary">
                I already have an account
            </Link>
        );
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <header className="bg-white border-b border-gray-200">
                <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-3 text-gray-900">
                        <img src="/logo.png" alt="Resume Builder" className="h-8 w-8" />
                        <span className="text-lg font-semibold">Resume Builder</span>
                    </Link>
                    <div className="flex items-center gap-3">
                        <ThemeToggle />
                        {isLoading ? (
                            <div className="btn btn-secondary opacity-70 cursor-wait">
                                Checking session...
                            </div>
                        ) : isAuthenticated ? (
                            <Link to="/dashboard" className="btn btn-primary">
                                Go to dashboard
                            </Link>
                        ) : (
                            <>
                                <Link to="/login" className="btn btn-secondary">
                                    Login
                                </Link>
                                <Link to="/register" className="btn btn-primary">
                                    Sign Up
                                </Link>
                            </>
                        )}
                    </div>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-6 py-12 space-y-12">
                <section className="grid md:grid-cols-2 gap-8 items-center">
                    <div className="space-y-5">
                        <h1 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight">
                            Build a job-ready resume in minutes.
                        </h1>
                        <p className="text-lg text-gray-600">
                            Write, tailor, and export ATS-friendly resumes with a clean editor and live preview.
                        </p>
                        <div className="flex flex-wrap gap-3">
                            {renderPrimaryCta()}
                            {renderSecondaryCta()}
                        </div>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                        <div className="text-sm text-gray-500 mb-2">Live preview</div>
                        <img
                            src="/landing-hero.svg"
                            alt="Resume Builder preview"
                            className="w-full max-h-80 object-contain"
                        />
                    </div>
                </section>

                <section className="grid md:grid-cols-3 gap-6">
                    <div className="card space-y-3">
                        <Sparkles size={20} className="text-primary-600" />
                        <h3 className="text-lg font-semibold">Tailor to each job</h3>
                        <p className="text-sm text-gray-600">
                            Generate cover letters and refine your resume using the job description.
                        </p>
                    </div>
                    <div className="card space-y-3">
                        <ShieldCheck size={20} className="text-primary-600" />
                        <h3 className="text-lg font-semibold">ATS friendly</h3>
                        <p className="text-sm text-gray-600">
                            Clean layouts and semantic structure to keep your resume readable by ATS systems.
                        </p>
                    </div>
                    <div className="card space-y-3">
                        <Download size={20} className="text-primary-600" />
                        <h3 className="text-lg font-semibold">Export instantly</h3>
                        <p className="text-sm text-gray-600">
                            Download your resume as PDF or DOCX whenever you are ready.
                        </p>
                    </div>
                </section>

                <section className="grid md:grid-cols-2 gap-6 items-center">
                    <div className="card space-y-3">
                        <h3 className="text-xl font-semibold text-gray-900">How it works</h3>
                        <ul className="space-y-3 text-sm text-gray-600">
                            <li><span className="font-semibold text-gray-900">1.</span> Import or start from a blank resume.</li>
                            <li><span className="font-semibold text-gray-900">2.</span> Paste a job description and tailor instantly.</li>
                            <li><span className="font-semibold text-gray-900">3.</span> Export PDF or DOCX with one click.</li>
                        </ul>
                    </div>
                    <div className="card space-y-3">
                        <h3 className="text-xl font-semibold text-gray-900">Designed for focus</h3>
                        <p className="text-sm text-gray-600">
                            No clutter. A clean editor, live preview, and a single dashboard to manage every resume.
                        </p>
                        <div className="grid grid-cols-3 gap-4 text-sm text-gray-600">
                            <div>
                                <div className="text-lg font-semibold text-gray-900">10+</div>
                                templates ready
                            </div>
                            <div>
                                <div className="text-lg font-semibold text-gray-900">1 click</div>
                                export
                            </div>
                            <div>
                                <div className="text-lg font-semibold text-gray-900">ATS</div>
                                friendly
                            </div>
                        </div>
                    </div>
                </section>

                <section className="grid md:grid-cols-3 gap-6">
                    <div className="card space-y-2">
                        <h4 className="text-base font-semibold text-gray-900">Smart tailoring</h4>
                        <p className="text-sm text-gray-600">
                            Align your resume to each role without inventing experience.
                        </p>
                    </div>
                    <div className="card space-y-2">
                        <h4 className="text-base font-semibold text-gray-900">Track every version</h4>
                        <p className="text-sm text-gray-600">
                            Keep a history of tailored resumes and download on demand.
                        </p>
                    </div>
                    <div className="card space-y-2">
                        <h4 className="text-base font-semibold text-gray-900">Cover letters included</h4>
                        <p className="text-sm text-gray-600">
                            Generate a clean cover letter and export it as a PDF.
                        </p>
                    </div>
                </section>
            </main>

            <footer className="border-t border-gray-200 bg-white">
                <div className="max-w-6xl mx-auto px-6 py-4 text-sm text-gray-500">
                    (c) {new Date().getFullYear()} Resume Builder. All rights reserved.
                </div>
            </footer>
        </div>
    );
}
